import { useRef, useCallback } from "react";
import { Check, Expand, Upload, Loader2, Sparkles, ArrowRight, ImagePlus } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AdStyle } from "./types";
import { dictionary, Language } from "@/utils/dictionary";

interface CustomReferenceCardProps {
  style: AdStyle;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onExpand?: (style: AdStyle) => void;
  disabled?: boolean;
  previewUrl?: string;
  onFileSelect: (file: File) => void;
  onRemoveFile: () => void;
  isLoading?: boolean;
  language: Language;
}

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export function CustomReferenceCard({
  style,
  isSelected,
  onToggle,
  onExpand,
  disabled,
  previewUrl,
  onFileSelect,
  onRemoveFile,
  isLoading = false,
  language,
}: CustomReferenceCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        alert(dictionary["only-jpg-png-webp-allowed"]?.[language as Language] || "Only JPG, PNG or WEBP files are allowed");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(dictionary["file-max-5mb"]?.[language as Language] || "File cannot exceed 5MB");
        return;
      }
      onFileSelect(file);
      e.target.value = "";
    },
    [onFileSelect, language]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (!ACCEPTED_TYPES.includes(file.type)) {
        alert(dictionary["only-jpg-png-webp-allowed"]?.[language as Language] || "Only JPG, PNG or WEBP files are allowed");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(dictionary["file-max-5mb"]?.[language as Language] || "File cannot exceed 5MB");
        return;
      }
      onFileSelect(file);
    },
    [onFileSelect]
  );

  const handleMainClick = () => {
    if (previewUrl && isSelected) {
      onToggle(style.id);
    } else {
      if (!isSelected) onToggle(style.id);
      fileInputRef.current?.click();
    }
  };

  const hasImage = previewUrl && isSelected;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={handleFileChange}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={handleMainClick}
        onKeyDown={(e) => e.key === "Enter" && handleMainClick()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className={cn(
          "break-inside-avoid relative group cursor-pointer",
          "rounded-xl overflow-hidden transition-all duration-200",
          isSelected
            ? "ring-2 ring-primary ring-offset-2"
            : "hover:scale-[1.02]",
          disabled && !isSelected && "opacity-40 cursor-not-allowed"
        )}
      >
        {hasImage ? (
          <img
            src={previewUrl}
            alt="Referencia"
            className="w-full block object-cover"
            style={{ aspectRatio: style.aspectRatio || "1/1" }}
          />
        ) : (
          /* Empty state: visual mockup */
          <div
            className={cn(
              "w-full flex flex-col items-center justify-center gap-3 px-4 py-5",
              "border-2 border-dashed transition-all duration-200",
              "bg-gradient-to-br from-[#F8F7FF] to-[#EEF0FF]",
              isSelected
                ? "border-primary/40"
                : "border-muted-foreground/20 group-hover:border-primary/40 group-hover:from-[#F0EEFF] group-hover:to-[#E8EAFF]"
            )}
            style={{ aspectRatio: style.aspectRatio || "1/1" }}
          >
            {/* Title */}
            <p className={cn(
              "text-[11px] font-bold tracking-wide transition-colors",
              isSelected ? "text-primary" : "text-muted-foreground group-hover:text-primary"
            )}>
              {dictionary["your-style-your-product"]?.[language as Language] || "Your style, your product"}
            </p>

            {/* Mini mockup: two cards + arrow */}
            <div className="flex items-center gap-2">
              {/* Reference card */}
              <div className="w-12 h-14 rounded-lg bg-gray-200/80 border border-gray-300/60 flex flex-col items-center justify-center gap-1 flex-shrink-0">
                <ImagePlus className="h-3.5 w-3.5 text-gray-400" strokeWidth={1.5} />
                <span className="text-[7px] text-gray-400 font-medium leading-none">{dictionary["ref"]?.[language as Language] || "Ref."}</span>
              </div>

              {/* Arrow with sparkles */}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Sparkles className={cn(
                  "h-3 w-3 transition-colors",
                  isSelected ? "text-primary" : "text-muted-foreground/50 group-hover:text-primary"
                )} />
                <ArrowRight className={cn(
                  "h-3 w-3 transition-colors",
                  isSelected ? "text-primary" : "text-muted-foreground/50 group-hover:text-primary"
                )} />
              </div>

              {/* Product card */}
              <div className="w-12 h-14 rounded-lg bg-[#EEF0FF] border border-[#D8DAFF]/60 flex flex-col items-center justify-center gap-1 flex-shrink-0">
                <Sparkles className="h-3.5 w-3.5 text-fluxi/60" strokeWidth={1.5} />
                <span className="text-[7px] text-fluxi/70 font-medium leading-none">{dictionary["your-ad"]?.[language as Language] || "Your ad"}</span>
              </div>
            </div>

            {/* CTA text */}
            <div className="text-center">
              <p className={cn(
                "text-[10px] font-semibold transition-colors",
                isSelected ? "text-primary" : "text-foreground/80 group-hover:text-primary"
              )}>
                {dictionary["upload-an-ad-you-like"]?.[language as Language] || "Upload an ad you like"}
              </p>
              <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                {dictionary["ai-replicates-style-with-product"]?.[language as Language] || "AI replicates its style with your product"}
              </p>
            </div>
          </div>
        )}

        {hasImage && (
          <>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-white font-bold text-sm tracking-wide uppercase drop-shadow-lg">
                {dictionary["custom"]?.[language as Language] || "Personalizada"}
              </span>
            </div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 pointer-events-none" />
          </>
        )}

        {hasImage && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onExpand?.(style);
            }}
            className="absolute top-2 right-2 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/60 z-10"
            title={dictionary["view-preview"]?.[language as Language] || "View preview"}
          >
            <Expand className="w-3.5 h-3.5 text-white" strokeWidth={2} />
          </button>
        )}

        {hasImage && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            className="absolute bottom-2 right-2 w-7 h-7 bg-black/40 backdrop-blur-sm rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-black/60 z-10"
            title={dictionary["change-image"]?.[language as Language] || "Change image"}
          >
            <Upload className="w-3.5 h-3.5 text-white" strokeWidth={2} />
          </button>
        )}

        {isSelected && (
          <div className="absolute top-2 left-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center z-10 shadow-md">
            <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
          </div>
        )}

        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-background/80 backdrop-blur-sm">
            <Loader2 className="h-8 w-8 animate-spin text-primary" strokeWidth={2} />
          </div>
        )}
      </div>
    </>
  );
}
